/**
 * @file   : SearchableGrid.js
 * @author : Amarjeet Lamba (Amarjeet.Lamba@AspiraConnect.com)
 * @date   : 4/12/2019, 4:13:15 PM
 */
import PropTypes from 'prop-types'
import React, { Component } from 'react'

import SimpleAnimate, {
  CMP_TYPE_DIV,
} from '../../../external/components/animation/SimpleAnimate'
import DynamicGrid from '../../../external/components/container/DynamicGrid'
import HighlightContainer, {
  HIGHLIGHT_IMPORTANT,
} from '../../../external/components/container/HighlightContainer'
import SimpleContainer from '../../../external/components/container/SimpleContainer'
import FlipCard from '../../../external/components/container/flipCard/FlipCard'
import TypeaheadCmp from '../../../external/components/form/typeahead/TypeaheadCmp'
import ACol from '../../../external/components/layout/ACol'
import AGrid from '../../../external/components/layout/AGrid'
import ARow from '../../../external/components/layout/ARow'
import Cont from '../../../external/components/layout/Cont'
import Section from '../../../external/components/layout/Section'
import AIconAction from '../../../external/components/link/AIconAction'
import TextStyler, {
  DARK_BLUE_TEXT,
  FONT_WEIGHT,
} from '../../../external/components/text/TextStyler'
import { isEmpty, isUndefined } from '../../../external/utilities/GeneralUtils'
import { ROLES } from '../../../external/utilities/accessibility/Definitions'
import { simpleTranslate, translate } from '../../Translation'
import SimpleImageCard from '../../shared/cards/SimpleImageCard'
import HackMarketingContentWrapper from '../../sharedInternal/container/HackMarketingContentWrapper'
import Padder, { PAD_10 } from '../../sharedInternal/container/Padder'
import { getTranslation, isData } from '../InfoUtil'
import SectionAd from './SectionAd'

class SearchableGrid extends Component {
  constructor(props, context) {
    super(props, context)
    this.state = {
      selectedItems: SearchableGrid.getAllItems(props.section),
      searchCritera: [],
      itemSearchResults: [],
      selectedSearchTerm: '',
    }
  }

  componentDidUpdate(prevProps) {
    const { section } = this.props

    if (section !== prevProps.section) {
      this.setState({
        selectedItems: SearchableGrid.getAllItems(section),
      })
    }
  }

  // ****************search handlers********************************
  onSearchTermChange(searchTerm) {
    this.setState({
      ...this.state,
      itemSearchResults: this.getMatchingSearchCriteria(searchTerm),
    })
  }

  onSearchResultItemSelectRequest(selectedItem) {
    const searchCritera = []
    this.setState({
      ...this.state,
      selectedItems: this.updateSelectedItems([selectedItem]),
      searchCritera,
      selectedSearchTerm: selectedItem.name,
    })
  }

  onSearchTrigger(selectedSearchTerm, selectedSearchItem) {
    const { section } = this.props
    let searchCritera = []
    let selectedItems = SearchableGrid.getAllItems(section)
    let currSelectedSearchTerm = ''
    if (!isEmpty(selectedSearchItem)) {
      selectedItems = this.updateSelectedItems([selectedSearchItem])
      currSelectedSearchTerm = selectedSearchItem
    } else if (!isEmpty(selectedSearchTerm)) {
      searchCritera = this.getMatchingSearchCriteria(selectedSearchTerm)
      selectedItems = this.updateSelectedItems(searchCritera)
      currSelectedSearchTerm = selectedSearchTerm
    }

    this.setState({
      ...this.state,
      selectedItems,
      searchCritera,
      selectedSearchTerm: currSelectedSearchTerm,
    })
  }

  onClearSearchRequest() {
    const { section } = this.props
    this.setState({
      ...this.state,
      selectedItems: SearchableGrid.getAllItems(section),
      selectedSearchTerm: '',
    })
  }

  // ****************search helper methods********************************
  updateSelectedItems(selectedSearchItems) {
    const { section } = this.props
    const selectedItems = []
    if (!isUndefined(section.content) && !isEmpty(section.content)) {
      for (const content of section.content) {
        if (
          SearchableGrid.isSearchItemsInContent(content, selectedSearchItems)
        ) {
          selectedItems.push(content)
        }
      }
    }

    return selectedItems
  }

  getMatchingSearchCriteria(searchTerm) {
    const { section } = this.props
    const results = []
    if (!isEmpty(searchTerm) && !isUndefined(section.content)) {
      for (const content of section.content) {
        if (!isUndefined(content.searchKeywords)) {
          for (const searchKeyword of content.searchKeywords) {
            if (
              searchKeyword.toLowerCase().startsWith(searchTerm.toLowerCase())
            ) {
              SearchableGrid.addItemSearchResults(results, {
                name: searchKeyword,
              })
            }
          }
        }
      }
    }

    return results
  }

  // ****************static methods********************************
  static getAllItems(section) {
    if (isEmpty(section) || isUndefined(section.content)) {
      return []
    }

    return section.content
  }

  static addItemSearchResults(itemSearchResults, searchResult) {
    let added = false
    if (!isUndefined(itemSearchResults)) {
      for (const currSearchResult of itemSearchResults) {
        if (currSearchResult.name === searchResult.name) {
          added = true
          break
        }
      }
      if (!added) {
        itemSearchResults.push(searchResult)
      }
    }
  }

  static isSearchItemsInContent(content, selectedSearchItems) {
    let isPresent = false
    if (
      !isUndefined(selectedSearchItems) &&
      !isEmpty(selectedSearchItems) &&
      !isUndefined(content.searchKeywords)
    ) {
      for (const currSelectedSearchItem of selectedSearchItems) {
        for (const searchKeyword of content.searchKeywords) {
          if (
            searchKeyword.toLowerCase() ===
            currSelectedSearchItem.name.toLowerCase()
          ) {
            isPresent = true
            break
          }
        }
        if (isPresent) {
          break
        }
      }
    }

    return isPresent
  }

  static getLabelValueStyledText(label = '', value = '') {
    return (
      <Cont asSpan>
        <TextStyler
          styleObj={{
            ...DARK_BLUE_TEXT,
            fontWeight: FONT_WEIGHT.BOLD,
          }}
        >
          {label}
        </TextStyler>
        <TextStyler styleObj={{ ...DARK_BLUE_TEXT }}>{value}</TextStyler>
      </Cont>
    )
  }

  // ****************ui methods********************************
  getGridCellUI(cellContent) {
    const { infoData, intl } = this.props
    if (isEmpty(cellContent)) {
      return <Cont></Cont>
    }

    const cardFront = (
      <SimpleImageCard
        title={getTranslation(
          intl,
          cellContent.token,
          cellContent.default,
          infoData.infoToken,
        )}
        imageURL={infoData.baseImagesFolder + cellContent.imageURL}
        imageLabel={getTranslation(
          intl,
          cellContent.imageDescription.token,
          cellContent.imageDescription.default,
          infoData.infoToken,
        )}
        linkURL={cellContent.link}
        linkNewWin={cellContent.newWindow}
        linkInternal={cellContent.linkInternal}
      />
    )

    return (
      <FlipCard
        isShowMoreButton={false}
        cardSize={'1x'}
        cardContentFront={cardFront}
      />
    )
  }

  getClientsUI() {
    const {
      searchCritera,
      itemSearchResults,
      selectedSearchTerm,
      selectedItems,
    } = this.state
    const { intl, colXs, colSm, colMd, colLg } = this.props
    const searchCriteriaNames = []

    if (!isEmpty(searchCritera)) {
      for (const currSearchCriteria of searchCritera) {
        searchCriteriaNames.push(currSearchCriteria.name)
      }
    }
    let currSearchUI = null
    let clientsUI = null
    let searchUI = null

    searchUI = (
      <AGrid fluid>
        <ARow>
          <ACol xs={12} sm={6} md={6} lg={6}></ACol>
          <ACol xs={12} sm={6} md={6} lg={6}>
            <TypeaheadCmp
              showSearchTrigger
              onSearchTrigger={this.onSearchTrigger.bind(this)}
              onSearchTermChange={this.onSearchTermChange.bind(this)}
              allQuickSearchResults={itemSearchResults}
              onSearchResultItemSelectRequest={this.onSearchResultItemSelectRequest.bind(
                this,
              )}
              resetOnSelect={false}
              placeHolderMsg={simpleTranslate(
                intl,
                'contact_us_search_placeholder',
              )}
            />
          </ACol>
        </ARow>
      </AGrid>
    )

    if (!isEmpty(selectedSearchTerm)) {
      currSearchUI = (
        <SimpleAnimate componentType={CMP_TYPE_DIV}>
          <Padder padBottom={PAD_10}>
            <HighlightContainer style={HIGHLIGHT_IMPORTANT}>
              <Cont cmpRole={ROLES.WIDGET.ALERT}>
                <Cont>
                  <AIconAction
                    onClick={this.onClearSearchRequest.bind(this)}
                    isShowAsLink
                    asSpan
                    faStyle={'fa fa-remove'}
                    cmpLabel={simpleTranslate(intl, 'label_clear_search')}
                  />
                  {SearchableGrid.getLabelValueStyledText(
                    simpleTranslate(intl, 'label_search_results'),
                    `"${selectedSearchTerm}" `,
                  )}
                </Cont>
                <Cont>
                  {isEmpty(searchCriteriaNames)
                    ? null
                    : SearchableGrid.getLabelValueStyledText(
                        translate(intl, 'label_search_results_matches'),
                        searchCriteriaNames.join(', '),
                      )}
                </Cont>
                <Cont>
                  {isEmpty(selectedItems)
                    ? translate(intl, 'label_search_results_empty')
                    : null}
                </Cont>
              </Cont>
            </HighlightContainer>
          </Padder>
        </SimpleAnimate>
      )
    }
    if (!isEmpty(selectedItems)) {
      clientsUI = (
        <DynamicGrid
          colXs={colXs}
          colSm={colSm}
          colMd={colMd}
          colLg={colLg}
          items={selectedItems}
          getCellContent={this.getGridCellUI.bind(this)}
          fluid
        />
      )
    }

    return (
      <SimpleContainer>
        <Cont>
          {searchUI}
          {currSearchUI}
          {clientsUI}
        </Cont>
      </SimpleContainer>
    )
  }

  render() {
    const { section, mainConfig } = this.props
    if (isData(section) && section.showSection) {
      return (
        <HackMarketingContentWrapper>
          {this.getClientsUI()}
          <SectionAd section={section} mainConfig={mainConfig} />
          <Section />
        </HackMarketingContentWrapper>
      )
    }

    return null
  }

  static propTypes = {
    intl: PropTypes.object.isRequired,
    section: PropTypes.object,
    mainConfig: PropTypes.object,
    infoData: PropTypes.object,
    colXs: PropTypes.number,
    colSm: PropTypes.number,
    colMd: PropTypes.number,
    colLg: PropTypes.number,
  }

  static defaultProps = {
    section: {},
    mainConfig: {},
    infoData: {},
    colXs: 12,
    colSm: 6,
    colMd: 3,
    colLg: 3,
  }
}

export default SearchableGrid
